"use client";

import clsx from "clsx";
import type { EventLogState } from "@/lib/sse/useSSE";
import { Monogram } from "@/components/persona/Monogram";
import { ALL_MODULES } from "@/lib/modules";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Generic seller-side status view. Used when the presenter presses `E` /
 * `E-GP` on a screen whose natural perspective is the importer (onboarding,
 * LC composer / funding tile / gate cascade, etc.).
 *
 * The exporter doesn't have a deeply-different UI for these moments — they
 * see a "your counterparty is verifying / placing the LC / awaiting your
 * acceptance" status card, plus a notification feed mirroring the events
 * that have fired so far. This keeps the perspective swap honest (the
 * audience sees something different) without forcing us to build a full
 * counterpart UI for every importer-driven screen.
 *
 *   mode = "onboarding"        → Acts 1.x  (Al Madar verifying)
 *   mode = "escrow-in-progress" → Acts 3.1–3.3  (T1: Programmable Escrow, no bank)
 *   mode = "lc-in-progress"     → Acts 4.3–4.4  (T2: real LC, FAB-financed)
 *   mode = "t3-lc-in-progress"  → Acts 5.4–5.5  (T3: Pampagro placing the LC, Itaú-financed)
 */
export type ExporterMode = "onboarding" | "escrow-in-progress" | "lc-in-progress" | "t3-lc-in-progress";

const META: Record<ExporterMode, {
  exporterName: string;
  exporterFlag: string;
  importerName: string;
  importerFlag: string;
}> = {
  "onboarding":         { exporterName: "Aurora Aves Brasil",          exporterFlag: "BR", importerName: "Al Madar Foods LLC",      importerFlag: "AE" },
  "escrow-in-progress": { exporterName: "Aurora Aves Brasil",          exporterFlag: "BR", importerName: "Al Madar Foods LLC",      importerFlag: "AE" },
  "lc-in-progress":     { exporterName: "Aurora Aves Brasil",          exporterFlag: "BR", importerName: "Al Madar Foods LLC",      importerFlag: "AE" },
  "t3-lc-in-progress":  { exporterName: "Gulf Phosphate Industries UAE", exporterFlag: "AE", importerName: "Pampagro Agronegócios", importerFlag: "BR" },
};

const MODE_KEY: Record<ExporterMode, string> = {
  "onboarding":         "onboarding",
  "escrow-in-progress": "escrowInProgress",
  "lc-in-progress":     "lcInProgress",
  "t3-lc-in-progress":  "t3LcInProgress",
};

export function TradeInProgressExporter({
  ev,
  mode,
  className,
}: {
  ev: EventLogState;
  mode: ExporterMode;
  className?: string;
}) {
  const t        = useTranslations("tradeInProgressExporter");
  const meta     = META[mode];
  const mk       = MODE_KEY[mode];
  // Dynamic key lookup — mk is one of onboarding / escrowInProgress / lcInProgress / t3LcInProgress
  const tDyn     = t as (k: string, p?: Record<string, string | number>) => string;
  const headline = tDyn(`${mk}_headline`);
  const subline  = tDyn(`${mk}_subline`);
  const feedTitle = tDyn(`${mk}_feedTitle`);

  // Build a feed of the most recent events relevant to this mode.
  const feed = ev.events.slice(-40).filter((e) => {
    if (mode === "onboarding") {
      return /^(onboarding|verification|payment|identity|trade\.Proposal)/.test(e.eventType)
          || e.eventType === "ui.ModuleUnlocked";
    }
    return /^(lc|policy|trade\.Proposal|agent\.Spoke)/.test(e.eventType);
  }).slice(-6);

  // Module-progress mirror: which importer modules have unlocked (visible
  // signal of how far the buyer is in onboarding / trade prep).
  const modulesLit = Array.from(ev.modulesUnlocked);

  // "What Al Madar chose" callout — T1 onboarding-only summary that
  // surfaces the funding decision (Self-financed · Programmable Escrow)
  // and the plan tier (SME · USD 250 OrbID fee) to the exporter view.
  // Each line lights up once its triggering event has fired; until then
  // the row reads as muted "pending". Mirrors choices that live only on
  // the importer's UI surface (1.6 proposal popup, 1.7 plan picker) so
  // an E-perspective audience isn't kept in the dark.
  const proposalSubmitted = ev.events.some((e) => e.eventType === "trade.ProposalSubmitted");
  const paymentReconciled = ev.events.some((e) => e.eventType === "payment.Reconciled");

  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass p-5">
        <div className="flex items-start gap-4">
          <div className="flex items-center gap-3">
            <Monogram name={meta.exporterName} flag={meta.exporterFlag} size={36} accent="var(--state-verified)" />
            <span className="font-mono text-[10px] text-orbitra-muted">→</span>
            <Monogram name={meta.importerName} flag={meta.importerFlag} size={36} accent="var(--state-cold)" />
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("kickerYourView", { exporterName: meta.exporterName })}
            </p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{headline}</h3>
            <p className="mt-1 text-[12px] text-orbitra-teal-soft">{subline}</p>
          </div>
          <span className="state-traced-stripe ms-auto rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-traced">
            {t("badgeInProgress")}
          </span>
        </div>

        {mode === "onboarding" && (
          <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
            <ChoiceRow
              label={t("choiceFunding")}
              value={t("choiceFundingValue")}
              fired={proposalSubmitted}
              firedHint={t("choiceFiredHintFunding")}
            />
            <ChoiceRow
              label={t("choicePlanTier")}
              value={t("choicePlanTierValue")}
              fired={paymentReconciled}
              firedHint={t("choiceFiredHintPlanTier")}
            />
          </div>
        )}
      </div>

      <div className="grid flex-1 grid-cols-3 gap-4">
        <div className="glass col-span-2 flex flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{feedTitle}</p>
          {feed.length === 0 ? (
            <p className="mt-3 text-[12px] text-orbitra-muted italic">
              {t("awaitingSignal")}
            </p>
          ) : (
            <ul className="mt-3 flex flex-col gap-1.5 text-[11px]">
              {feed.map((e) => (
                <FeedRow key={e.eventId} type={e.eventType} payload={e.payload} />
              ))}
            </ul>
          )}

          <div className="mt-auto rounded-md border border-state-verified/20 bg-orbitra-ink/40 p-3 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("noActionRequired")}</p>
            <p className="mt-1 text-orbitra-paper">
              {mode === "onboarding"
                ? t("noActionOnboarding")
                : mode === "escrow-in-progress"
                  ? t("noActionEscrow")
                  : t("noActionLc")}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="glass p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("counterpartyModulesKicker", { count: modulesLit.length })}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-1 text-[10px]">
              {ALL_MODULES.map((m) => {
                const lit = modulesLit.includes(m);
                return (
                  <div key={m} className={clsx(
                    "flex items-center gap-1.5 rounded border px-2 py-1",
                    lit
                      ? "border-state-verified/40 bg-state-verified/10 text-state-verified"
                      : "border-orbitra-muted/20 text-orbitra-muted/70",
                  )}>
                    <span className={clsx(
                      "h-1.5 w-1.5 rounded-full",
                      lit ? "bg-state-verified" : "bg-orbitra-muted/40",
                    )} />
                    <span className="truncate">{m}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="glass p-4 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("whyYouDontSeeMore")}</p>
            <p className="mt-2 text-orbitra-paper">
              {mode === "escrow-in-progress" ? t("whyDontSeeMoreBodyEscrow") : t("whyDontSeeMoreBodyLc")}
              {" "}Press <kbd className="rounded border border-orbitra-muted/30 bg-orbitra-ink/60 px-1 py-0.5 font-mono text-[9px]">I</kbd> to peek their view.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ALL_MODULES is imported below (single source of truth in lib/modules.ts).

/**
 * ChoiceRow — a static row in the "What Al Madar chose" callout. Reads
 * muted/pending while `fired` is false (event hasn't arrived yet) and
 * flips to confirmed once the trigger event lands. Used so the E-view
 * surfaces choices that otherwise live only on the importer's UI.
 */
function ChoiceRow({
  label,
  value,
  fired,
  firedHint,
}: {
  label: string;
  value: string;
  fired: boolean;
  firedHint: string;
}) {
  const t = useTranslations("tradeInProgressExporter");
  return (
    <div
      className={clsx(
        "rounded-md border px-3 py-2",
        fired
          ? "border-state-verified/40 bg-state-verified/8"
          : "border-state-future/40 bg-state-future/8",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className={clsx(
          "font-mono text-[9px] uppercase tracking-[0.18em]",
          fired ? "text-state-verified" : "text-state-future",
        )}>
          {label}
        </p>
        <span className={clsx(
          "font-mono text-[9px] uppercase tracking-wider",
          fired ? "text-state-verified" : "text-state-future italic",
        )}>
          {fired ? t("choiceConfirmed") : t("choicePending")}
        </span>
      </div>
      <p className={clsx("mt-1", fired ? "text-orbitra-paper" : "text-state-future")}>
        {fired ? value : t("choiceAwaitingChoice")}
      </p>
      {fired && (
        <p className="mt-0.5 font-mono text-[9px] text-orbitra-muted">{firedHint}</p>
      )}
    </div>
  );
}

function FeedRow({ type, payload }: { type: string; payload: Record<string, unknown> }) {
  const t = useTranslations("tradeInProgressExporter");
  const summary = summarise(type, payload, t as (k: string, p?: Record<string, string | number>) => string);
  return (
    <li className="flex items-start gap-3 rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-1.5">
      <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-state-verified shrink-0" />
      <span className="flex-1 text-orbitra-paper">{summary}</span>
      <span className="font-mono text-[9px] text-orbitra-muted shrink-0">{type}</span>
    </li>
  );
}

function summarise(type: string, p: Record<string, unknown>, t: (k: string, p?: Record<string, string | number>) => string): string {
  switch (type) {
    case "onboarding.AccountCreated":            return t("sumAccountCreated");
    case "onboarding.EntityDocumentsUploaded":   return t("sumDocsUploaded");
    case "onboarding.OcrCompleted":              return t("sumOcrCompleted");
    case "onboarding.GleifLookupCompleted":      return t("sumGleifLookup");
    case "onboarding.AuthorizedIndividualAdded": return t("sumAuthIndividual");
    case "verification.LiveSessionStarted":      return t("sumLiveSessionStarted");
    case "verification.LiveSessionCompleted":    return t("sumLiveSessionCompleted");
    case "payment.Dispatched":                   return t("sumPaymentDispatched");
    case "payment.Reconciled":                   return t("sumPaymentReconciled");
    case "identity.OrbIDIssued":                 return t("sumOrbIdIssued");
    case "trade.ProposalDrafted":                return t("sumProposalDrafted");
    case "trade.ProposalSubmitted":              return t("sumProposalSubmitted");
    case "trade.ProposalAccepted":               return t("sumProposalAccepted");
    case "ui.ModuleUnlocked":                    return t("sumModuleUnlocked", { module: String(p.module ?? "") });

    case "lc.ComposerOpened":                    return p.bankBadge ? t("sumLcComposerOpened") : t("sumEscrowComposerOpened");
    case "lc.FundingMixSelected":                return p.bankFinancingPct
                                                   ? t("sumFundingMixBank", { buyer: String(p.buyerWorkingCapitalPct), bank: String(p.bankFinancingPct), bankBadge: String(p.bankBadge ?? "bank") })
                                                   : t("sumFundingMixNoBank", { buyer: String(p.buyerWorkingCapitalPct) });
    case "lc.Issued":                            return p.buyerSideBankBadge
                                                   ? t("sumLcIssued", { reference: String(p.lcReference), amount: (p.amountUsd as number)?.toLocaleString() ?? "" })
                                                   : t("sumEscrowIssued", { reference: String(p.lcReference), amount: (p.amountUsd as number)?.toLocaleString() ?? "" });
    case "policy.Decided":                       return t("sumGateDecided", { gate: String(p.gate), outcome: String(p.outcome) });
    case "agent.Spoke":                          return t("sumAgentSpoke", { agent: String(p.agent ?? "Agent"), line: (p.line as string)?.slice(0, 80) ?? "" });
    default:                                     return type;
  }
}
